import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { StatewidePloSessionListBoard } from '@api/BoardSDK';

// Groups visible to the public roster. Excludes the internal
// "FULL/Closed/Cancelled" group and the "Fake Tests - Delete" group.
const VIEWABLE_GROUPS = ['topics', 'group_mkxxpn0d'];

// Only sessions open for registration are shown on the public roster.
const VISIBLE_STATUSES = ['Open'];

const SESSION_COLUMNS = [
  'day1Date',
  'allSessionDatesLocation',
  'hostingSite',
  'location',
  'regFormStatus',
  'subjectManagedForAces',
  'gradeBandS',
  'facilitators',
  'workDescriptor',
  'creditHoursAsDayCount',
  'psSection',
] as const;

export const listSessions = createServerFn({ method: 'GET' })
  .validator(
    z.object({
      search: z.string().optional(),
      subject: z.string().optional(),
      hostingSite: z.array(z.string()).optional(),
      gradeBand: z.array(z.string()).optional(),
      dateFrom: z.string().optional(),
      dateTo: z.string().optional(),
      sortBy: z.enum(['day1Date', 'name', 'hostingSite']).default('day1Date'),
      sortDir: z.enum(['asc', 'desc']).default('asc'),
      cursor: z.string().nullable().optional(),
      limit: z.number().min(1).max(100).default(25),
    }),
  )
  .handler(async ({ data }) => {
    const board = new StatewidePloSessionListBoard();

    const where: Record<string, unknown> = { group: VIEWABLE_GROUPS, regFormStatus: VISIBLE_STATUSES };
    if (data.search) where.name = { contains: data.search };
    if (data.subject) where.subjectManagedForAces = data.subject;
    if (data.hostingSite && data.hostingSite.length > 0) where.hostingSite = data.hostingSite;
    if (data.gradeBand && data.gradeBand.length > 0) where.gradeBandS = data.gradeBand;
    // Always hide sessions whose first day has already passed; today still shows.
    // The user's own "from" date can only narrow this further, never widen it into the past.
    const today = new Date().toISOString().slice(0, 10);
    const from = data.dateFrom && data.dateFrom > today ? data.dateFrom : today;
    if (data.dateTo) {
      where.day1Date = { gte: from, lte: data.dateTo };
    } else {
      where.day1Date = { gte: from };
    }

    const sessionsRes = await board
      .items()
      .withColumns([...SESSION_COLUMNS])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .where(where as any)
      .orderBy({ column: data.sortBy, direction: data.sortDir })
      .withPagination({ limit: data.limit, cursor: data.cursor ?? undefined })
      .execute();

    return { items: sessionsRes.items, cursor: sessionsRes.cursor };
  });

export const getFilterOptions = createServerFn({ method: 'GET' })
  .validator(z.object({}).optional())
  .handler(async () => {
    const board = new StatewidePloSessionListBoard();

    const [subjectRows, siteRows, gradeBandRows] = await Promise.all([
      board.aggregate().groupBy('subjectManagedForAces').countItems('count').orderBy('subjectManagedForAces', 'asc').execute(),
      board.aggregate().groupBy('hostingSite').countItems('count').orderBy('hostingSite', 'asc').execute(),
      board.aggregate().groupBy('gradeBandS').countItems('count').orderBy('gradeBandS', 'asc').execute(),
    ]);

    return {
      subjects: (subjectRows ?? []).map((r) => r.subjectManagedForAces).filter(Boolean) as string[],
      hostingSites: (siteRows ?? []).map((r) => r.hostingSite).filter(Boolean) as string[],
      gradeBands: (gradeBandRows ?? []).map((r) => r.gradeBandS).filter(Boolean) as string[],
    };
  });
